import React, { useEffect, useState } from "react";
import { ColumnDetail } from "@avamae/table";
import { makeRow, Checkbox, FlexProps } from "./FlexTable";
import { Cell, CellOptions } from "./Cell";
import { toCamelCase } from "helpers/formatFormFieldNames";
import { lowerCaseFirstLetter } from "helpers/lowerCaseFirstLetter";
import { ScrollSyncPane } from "react-scroll-sync";

type LeftTableProps<T = any> = {
  idColumn: string;
  checkbox?: Checkbox;
  columns: ColumnDetail<any>[];
  columnData: any[];
  rowHovered: number | null;
  handleMouseEnterRow(e: any, i: number): void;
  onRowClick?(id: number): void;
  hiddenColumns?: string[];
  formatPrimitives(value: unknown): any;
  rowFormatter?(r: T): { [K in keyof T]: React.ReactNode };
  noRowHover?: boolean;
  columnCount: number;
  rowCellOptions?: { [K in keyof Partial<T>]: CellOptions };
  toggleColumnSort?: (k: string) => void;
  sortBy: { [k: string]: "ASC" | "DESC" };
  overflow: boolean;
  disableMultiSelect?: boolean;
  parentSelectedId?: number;
  reducedPadding?: boolean;
};

const LeftTable: React.FC<LeftTableProps & FlexProps> = ({
  idColumn,
  checkbox,
  columns,
  columnData,
  rowHovered,
  handleMouseEnterRow,
  onRowClick,
  hiddenColumns,
  formatPrimitives,
  rowFormatter,
  noRowHover,
  columnCount = 0,
  rowCellOptions = {},
  toggleColumnSort,
  sortBy,
  overflow,
  disableMultiSelect,
  menuActionsCode,
  setMenuActionsCode,
  parentSelectedId,
  twoSingleClicks,
  reducedPadding,
}) => {
  // Placeholder
  if ((columnCount <= 0 && !checkbox) || columns.length === 0) {
    // return null;
    return (
      <div
        className="LeftSideTable LeftSideTablePlaceholder"
        style={{ minHeight: "100%", width: "0px" }}
      >
        <div className="TableBody">
          {columnData &&
            columnData.map((item, idx) => {
              return (
                <div className="Row" key={`left-side-table-row-${idx}`}>
                  <div
                    className="Cell"
                    key={`left-side-table-row-cell-${idx}`}
                  ></div>
                </div>
              );
            })}
        </div>
      </div>
    );
  }

  const sliceBy = checkbox ? (columnCount <= 1 ? 1 : columnCount) : columnCount;
  const selectedColumns = columns
    .filter(c => !hiddenColumns?.includes(c.columnKey.toString().toLowerCase()))
    .slice(0, sliceBy);

  const processedRowData = columnData.map((data, i) => {
    let rowData = {} as any;
    selectedColumns.forEach(col => {
      rowData = {
        ...rowData,
        [toCamelCase(col.columnKey as string)]:
          data[toCamelCase(col.columnKey as string)] ?? data[col.columnKey],
      };
    });
    return { id: data[idColumn].toString(), ...rowData };
  });

  const allSelected = checkbox?.selectedIds.length === columnData.length;

  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (checkbox) {
      if (allSelected) {
        checkbox.setSelectedIds([]);
      } else {
        checkbox.setSelectedIds(
          columnData.map(data => data[idColumn].toString())
        );
      }
    }
  };

  const idColumnOptions =
    idColumn && rowCellOptions && rowCellOptions.hasOwnProperty(idColumn)
      ? rowCellOptions[idColumn as string]
      : null;

  const LeftSideTableHeadWidth =
    document.getElementById(`LeftSideTableHead-${idColumn}`)?.offsetWidth ?? 0;

  const LeftSideTableHeadHeight =
    document.getElementById(`LeftSideTableHead-${idColumn}`)?.offsetHeight ?? 0;

  return (
    <div
      className={`LeftSideTable ${overflow ? "BoxShadow" : ""} ${
        reducedPadding ? "ReducedPadding" : ""
      }`}
      id={`LeftSideTableContainer-${idColumn}`}
      style={{
        paddingTop: `${LeftSideTableHeadHeight}px`,
        width: `${LeftSideTableHeadWidth}px`,
        minWidth: `${LeftSideTableHeadWidth}px`,
        maxWidth: `${LeftSideTableHeadWidth}px`,
      }}
    >
      <div className="Head" id={`LeftSideTableHead-${idColumn}`}>
        {checkbox && !disableMultiSelect && (
          <Cell
            options={{
              cellType: "checkboxCell",
              ...idColumnOptions,
            }}
            key={`checkbox-cell-${idColumn}`}
          >
            <div>
              <input
                id="toggleSelectAll"
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              <label className="CustomInput" htmlFor="toggleSelectAll" />
            </div>
          </Cell>
        )}
        {selectedColumns.map((c, i) => {
          const sortByValue = sortBy[String(c.columnKey)] ?? "NONE";
          const isSorting = sortByValue === "ASC" || sortByValue === "DESC";
          const { bSortable } = c;

          const parsedColumnKey =
            typeof c.columnKey === "string"
              ? lowerCaseFirstLetter(c.columnKey)
              : c.columnKey;

          return (
            <Cell
              key={`cell-${i}`}
              options={rowCellOptions[parsedColumnKey as string]}
              sortable={bSortable}
              onClick={() =>
                bSortable &&
                toggleColumnSort &&
                toggleColumnSort(String(c.columnKey))
              }
              tooltipLabel={c.labelValue}
            >
              <span
                className={bSortable && isSorting ? "Sorting" : ""}
                key={`cell-span-${i}`}
              >
                {c.labelValue}
              </span>
              {bSortable && (
                <i
                  className={
                    sortByValue === "ASC"
                      ? "SortIcon ASC"
                      : sortByValue === "DESC"
                      ? "SortIcon DESC"
                      : "SortIcon"
                  }
                  key={`cell-i-${i}`}
                />
              )}
            </Cell>
          );
        })}
      </div>
      <ScrollSyncPane>
        <div
          className="TableBody"
          id={`LeftSideTableBody-${idColumn}`}
          style={{
            width: `${LeftSideTableHeadWidth}px`,
            minWidth: `${LeftSideTableHeadWidth}px`,
            maxWidth: `${LeftSideTableHeadWidth}px`,
          }}
        >
          {processedRowData.map((d, i) =>
            makeRow({
              id: d.id,
              r: d,
              index: i,
              columns,
              formatPrimitives,
              hiddenColumns: hiddenColumns ?? [],
              rowHovered,
              handleMouseEnterRow,
              onRowClick: onRowClick
                ? () => onRowClick(columnData.find((_, j) => j === i)[idColumn])
                : null,
              rowButton: undefined,
              formatRow: rowFormatter,
              noRowHover,
              checkbox,
              rowSelected:
                checkbox?.selectedIds.includes(d.id) ||
                checkbox?.selectedIds.includes(d.id.toString()) ||
                checkbox?.selectedIds.includes(parseInt(d.id, 10)),
              hideRowOptionComponent: checkbox
                ? checkbox.selectedIds.length > 0
                : false,
              rowCellOptions,
              includeCheckbox: true,
              disableMultiSelect,
              menuActionsCode,
              setMenuActionsCode,
              parentSelectedId,
              twoSingleClicks,
              position: "Left",
            })
          )}
        </div>
      </ScrollSyncPane>
    </div>
  );
};

export { LeftTable };
